import { useEffect, useMemo, useRef, useState } from "react";
import {
  BiSearch,
  MdQueue,
  FaFileExcel,
  HiOutlineTrash,
  HiOutlinePencilAlt,
  MdRemoveRedEye,
} from "react-icons/all";
import {
  Button,
  HStack,
  IconButton,
  Input,
  Grid,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Heading,
  Divider,
  ModalBody,
  VStack,
  ModalFooter,
  useToast,
  Box,
  Text,
} from "@chakra-ui/react";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { useForm } from "react-hook-form";
import { Spinner } from "../../../../components/Loader";
import { useAuth } from "../../../../plugins/CookieAuthPlugin";
import { InfiniteScrollTable } from "../../../../components/Tables";
import {
  FloatLabel,
  InputControl,
  SelectControl,
} from "../../../../components/Controls";
import { sleep, toastGenerator } from "../../../../utils/helper";
import { fullBorderExcel, nationalityTable } from "../../../../constants/tableData";
import { invalidateFindNationalityQuery, invalidateNationalityInfiniteQuery, invalidateNationalityQuery, useAddNationality, useDeleteNationality, useEditNationality, useFindNationality, useGetNationalities, useInfiniteFetchNationality } from "../../../../utils/hooks/nationality";
import { Redirect, Switch } from "react-router-dom/cjs/react-router-dom.min";
import { useDelayRouteExit } from "../../../../utils/hooks";
import { useGetRegionDropDown } from "../../../../utils/hooks/dropDown";

export default function Nationality() {
  const toast = useToast();
  const [modalType, setModalType] = useState("");
  const [selected, setSelected] = useState("");
  const [filter, setFilter] = useState({ searchText: "", regionId: "" });
  const [isExporting, setIsExporting] = useState(false);
  let exportButtonRef = useRef();
  const { user: { menuPermission } } = useAuth();
  const { isLeaving, isMount } = useDelayRouteExit(100);

  const isViewMode = modalType === "view";
  const isEditMode = modalType === "edit";

  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isExitModalOpen,
    onOpen: onExitModalOpen,
    onClose: onExitModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  const { handleSubmit, control, reset } = useForm();

  let isAllowAdd = menuPermission && menuPermission["nationality"]["ADD"];
  let isAllowEdit = menuPermission && menuPermission["nationality"]["EDIT"];
  let isAllowDelete = menuPermission && menuPermission["nationality"]["DELETE"];

  //drop down
  const regionDropDown = useGetRegionDropDown({
    payload: { page: 1, rowsPerPage: 1000 },
  });

  const nationalityInfiniteFetch = useInfiniteFetchNationality({
    payload: {
      rowsPerPage: 20,
      searchText: filter?.searchText,
      regionId: filter?.regionId,
    },
    config: {
      getNextPageParam: (lastPage) =>
        lastPage.pagination &&
        (lastPage.pagination.rowsPerPage * lastPage.pagination.page <
        lastPage.pagination.total
          ? lastPage.pagination.page + 1
          : false),
      cacheTime: 300000,
      staleTime: 1
    },
  });
  const nationalityFetch = useGetNationalities({
    payload: {
      page: 1,
      rowsPerPage: 10000,
      searchText: filter?.searchText,
      regionId: filter?.regionId,
    },
    config: {},
  });
  const findNationality = useFindNationality({
    payload: selected?.id,
    config: {
      staleTime: 1,
      enabled: !!selected?.id,
    },
  });

  useEffect(() => {
    (isEditModalOpen || onEditModalOpen) && reset(findNationality.data);
    //eslint-disable-next-line
  }, [findNationality.data]);


  const addNationalityMutation = useAddNationality({
    onSuccess(data) {
      if (data) {
        toast(
          toastGenerator({
            status: data.body.status ? "success" : "warning",
            message: data.body.message,
          })
        );
        if (data.body.status) {
          invalidateNationalityQuery();
          invalidateNationalityInfiniteQuery()
          onAddModalClose();
        }
      }
    },
    onError() {
      toast(toastGenerator({ status: "error" }));
    },
  });

  const editNationalityMutation = useEditNationality({
    onSuccess(data) {
      if (data) {
        toast(
          toastGenerator({
            status: data.body.status ? "success" : "warning",
            message: data.body.message,
          })
        );
        if (data.body.status) {
          invalidateNationalityQuery();
          invalidateNationalityInfiniteQuery()
          onEditModalClose();
          invalidateFindNationalityQuery();
        }
      }
    },
    onError() {
      toast(toastGenerator({ status: "error" }));
    },
  });

  const deleteNationalityMutation = useDeleteNationality({
    onSuccess(data) {
      if (data) {
        toast(
          toastGenerator({ status: data.body.status ? "success" : "warning" })
        );
        if (data.body.status) {
          invalidateNationalityQuery();
          invalidateNationalityInfiniteQuery()
          onDeleteModalClose();
        }
      }
    },
  });


  useEffect(() => {
    isExporting && !nationalityFetch.isLoading && exportButtonRef.current?.save();
    return setTimeout(() => setIsExporting(false), 1000);
  }, [isExporting, nationalityFetch.isLoading]);

  const onSubmit = (values) => {
    const jsonData = {
      id: selected?.id ? selected?.id : null,
      nameKh: values.nameKh,
      nameEn: values.nameEn,
      // nameCn: "",
      regionId: values.region?.value,
    };
    jsonData.id
      ? editNationalityMutation.mutate(jsonData)
      : addNationalityMutation.mutate(jsonData);
  };

  const columns = useMemo(
    () => [
      {
        Header: "No",
        accessor: "n",
      },
      {
        Header: "Region",
        accessor: "region",
      },
      {
        Header: "Name In KH",
        accessor: "nameInKH",
      },
      {
        Header: "Name In EN",
        accessor: "nameInEN",
      },
      {
        Header: "Created By",
        Cell: ({ row: { original } }) => (
          <Box>
            <Text>{original.createdBy}</Text>
            <Text>{original.createdDate}</Text>
            <Text>{original.createdTime}</Text>
          </Box>
        ),
      },
      {
        Header: "Modified By",
        Cell: ({ row: { original } }) => (
          <Box>
            <Text>{original.modifiedBy}</Text>
            <Text>{original.modifiedDate}</Text>
            <Text>{original.modifiedTime}</Text>
          </Box>
        ),
      },
      {
        Header: "Action",
        Cell: ({ row: { original } }) => (
          <HStack spacing="1">
            <IconButton
              onClick={() => {
                setSelected(original);
                setModalType("view");
                onEditModalOpen();
              }}
              variant="ghost"
              color="gray.500"
              size="sm"
              icon={<MdRemoveRedEye size="1.3rem" />}
            />
            <IconButton
              hidden={!isAllowEdit}
              onClick={() => {
                setSelected(original);
                setModalType("edit");
                onEditModalOpen();
              }}
              variant="ghost"
              color="gray.500"
              size="sm"
              icon={<HiOutlinePencilAlt size="1.3rem" />}
            />
            <IconButton
              hidden={!isAllowDelete}
              onClick={() => {
                setSelected(original);
                setModalType("delete");
                onDeleteModalOpen();
              }}
              size="sm"
              variant="ghost"
              color="gray.500"
              icon={<HiOutlineTrash size="1.3rem" />}
            />
          </HStack>
        ),
      },
    ],
    //eslint-disable-next-line
    []
  );

  const AddEditSection = () => {
    return (
      <Modal
        isCentered
        size="md"
        closeOnOverlayClick={false}
        isOpen={selected ? isEditModalOpen : isAddModalOpen}
        onClose={() => {
          if (selected) {
            setSelected("");
            onEditModalClose();
          }
          onAddModalClose();
        }}
      >
        <ModalOverlay />
        <ModalContent as="form" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <Heading fontSize="xl" fontWeight="medium">
              {isViewMode
                ? "View Nationality "
                : isEditMode
                ? "Edit Nationality"
                : "Add New Nationality"}
            </Heading>
          </ModalHeader>
          <Divider />
          <ModalBody>
            {findNationality.isLoading ? (
              <Spinner />
            ) : (
              <Grid py="2" templateColumns="repeat(1,1fr)" gap="4" px="3">
                <VStack spacing={4} mt="10px">
                  <InputControl
                    name="nameKh"
                    label="Name In Kh"
                    control={control}
                    placeholder="Enter Name"
                    isDisabled={isViewMode}
                    isRequired
                  />
                  <InputControl
                    name="nameEn"
                    label="Name In En"
                    control={control}
                    placeholder="Enter Name"
                    isDisabled={isViewMode}
                    isRequired
                  />
                  <SelectControl
                    name="region"
                    label="Region"
                    placeholder="Select"
                    control={control} 
                    isRequired
                    isDisabled={isViewMode}
                    options={regionDropDown.data}
                  />
                </VStack>
              </Grid>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={() => {
                if (!isViewMode) {
                  onExitModalOpen();
                } else {
                  setSelected("");
                  onEditModalClose();
                  onAddModalClose();
                }
              }}
            >
              Cancel
            </Button>
            {!isViewMode && (
              <Button
                type="submit"
                isLoading={
                  selected
                    ? editNationalityMutation.isLoading
                    : addNationalityMutation.isLoading
                }
                colorScheme="blue"
                ml={3}
              >
                {selected ? "Update" : "Create"}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };


  const DelectSection = () => {
    return (
      <AlertDialog
        motionPreset="slideInBottom"
        isOpen={isDeleteModalOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg">Delete Nationality?</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to delete this record?
          </AlertDialogBody>
          <AlertDialogFooter>
            <HStack spacing="2">
              <Button onClick={onDeleteModalClose}>No</Button>
              <Button
                colorScheme="red"
                isLoading={deleteNationalityMutation.isLoading}
                onClick={() => {
                  deleteNationalityMutation.mutate(selected?.id);
                }}
              >
                Yes
              </Button>
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  };

  return (
    <Switch>
    {Boolean(menuPermission["nationality"]) ? (
       <>
       <AlertDialog
         motionPreset="slideInBottom"
         isOpen={isExitModalOpen}
         isCentered
       >
         <AlertDialogOverlay />
         <AlertDialogContent>
           <AlertDialogHeader fontSize="lg">Comfirm Message</AlertDialogHeader>
           <AlertDialogBody>
             Are you sure you want to exit this page?
           </AlertDialogBody>
           <AlertDialogFooter>
             <HStack spacing="2">
               <Button onClick={onExitModalClose}>No</Button>
               <Button
                 colorScheme="red"
                 onClick={() => {
                   setSelected("");
                   onExitModalClose();
                   onAddModalClose();
                   onEditModalClose();
                 }}
               >
                 Yes
               </Button>
             </HStack>
           </AlertDialogFooter>
         </AlertDialogContent>
       </AlertDialog>
 
       <Flex flexDir="column" bg="white" h="full" rounded="md">
         <Grid
           as="form"
           templateColumns="auto max-content"
           p="3"
           mb="3"
           boxShadow="sm"
           onSubmit={(e) => {
             e.preventDefault();
             const formData = new FormData(e.target);
             const formProps = Object.fromEntries(formData);
             setFilter((prev) => ({
               ...prev,
               searchText: formProps.searchText,
               regionId: formProps.regionId,
             }));
           }}
         >
           <Grid templateColumns="repeat(2,13vw) max-content" gap="2">
             <SelectControl
                 isClearable
                 name="regionId"
                 label="Region"
                 control={control}
                 placeholder="Select"
                 options={regionDropDown.data}
               />
             <FloatLabel label="Name">
               <Input name="searchText" placeholder="Enter Name" />
             </FloatLabel>
             <IconButton
               type="submit"
               colorScheme="brand"
               isLoading={nationalityFetch.isLoading}
               icon={<BiSearch fontSize="1.2rem" />}
             />
           </Grid>
           <Grid>
             <HStack>
               <IconButton
                 isLoading={isExporting}
                 onClick={async () => {
                   nationalityFetch.refetch();
                   await sleep(1);
                   setIsExporting(true);
                 }}
                 icon={<FaFileExcel fontSize="1.2rem" />}
                 colorScheme="brand"
               />
               <Button
                 hidden={!isAllowAdd}
                 leftIcon={<MdQueue fontSize="1.5rem" />}
                 colorScheme="brand"
                 onClick={async () => {
                   reset({});
                   onAddModalOpen();
                   setModalType("add");
                   setSelected("");
                   await sleep(1)
                 }}
               >
                 Add New
               </Button>
             </HStack>
           </Grid>
         </Grid>
         {AddEditSection()}
         {DelectSection()}

          {!isLeaving && isMount && (
            <Box flex="1" overflow="auto">
              <InfiniteScrollTable
                columns={columns}
                {...nationalityInfiniteFetch}
              />
            </Box>
          )}
 
          <ExcelExport
            data={nationalityFetch.data}
            fileName="nationality_export"
            ref={exportButtonRef}
          >
            {nationalityTable?.map((item) => (
              <ExcelExportColumn
                title={item.label}
                field={item.columnId}
                locked={
                  item.columnId === "n" ||
                  item.columnId === "nameInKH" ||
                  item.columnId === "nameInEN"
                }
                headerCellOptions={{
                  ...fullBorderExcel,
                  bold: true,
                  background: "#CACCED",
                  fontSize: 12,
                  textAlign: "center",
                  color: "black",
                  verticalAlign: "center",
                  fontFamily: "Khmer OS Siemreap",
                }}
                cellOptions={{
                  ...fullBorderExcel,
                  textAlign: item.align,
                }}
                width={item.width}
              ></ExcelExportColumn>
            ))}
          </ExcelExport>
       </Flex>
     </>
    ) : (
        <Redirect to={`/`} />
    )}</Switch>
   
  );
}
